<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'dashboard/composables/useI18n';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useStore } from '../composables/store';

const { t } = useI18n();

const RECONNECTED_BANNER_TIMEOUT = 2000;
const store = useStore();
const messagePayloads = ref([]);
const messageData = ref({});
const messageTimeout = ref({});

/**
 * @param message {Record<string, any> | FormData}
 * @returns {string | null}
 */
const echoIdFrom = message => {
  if (message instanceof FormData) {
    return message.get('echo_id') || null;
  }
  // eslint-disable-next-line no-console
  console.assert(typeof message === 'object' && !Array.isArray(message));
  return message.echo_id || null;
};

const upsertMessagePayload = payload => {
  const echoId = echoIdFrom(payload);
  if (!echoId) {
    throw new Error(`No echo id on payload ${payload}`);
  }
  if (!messagePayloads.value.find(e => echoIdFrom(e) === echoId)) {
    const newPayloads = [...messagePayloads.value];
    newPayloads.push(payload);
    messagePayloads.value = newPayloads;
    return newPayloads;
  }
  return messagePayloads.value;
};
const removeMessagePayload = payload => {
  const echoId = echoIdFrom(payload);
  if (!echoId) {
    throw new Error(`No echo id on payload ${payload}`);
  }
  if (messagePayloads.value.find(e => echoIdFrom(e) === echoId)) {
    const newPayloads = messagePayloads.value.filter(
      e => echoIdFrom(e) !== echoId
    );
    messagePayloads.value = newPayloads;
    return newPayloads;
  }
  return messagePayloads.value;
};
const removeTimeoutFor = payload => {
  const echoId = echoIdFrom(payload);
  if (!echoId) {
    throw new Error(`No echo id on payload ${payload}`);
  }
  if (echoId in messageTimeout.value) {
    const timeout = messageTimeout.value[echoId]
    if (timeout !== null && timeout !== undefined) {
      clearTimeout(timeout);
    }
    const newTimeouts = { ...messageTimeout.value };
    delete newTimeouts[echoId];
    messageTimeout.value = newTimeouts;
  }
  return messageTimeout.value;
};
const upsertTimeoutFor = (payload, timeout) => {
  removeTimeoutFor(payload);
  const echoId = echoIdFrom(payload);
  if (!echoId) {
    throw new Error(`No echo id on payload ${payload}`);
  }
  const newTimeouts = { ...messageTimeout.value };
  newTimeouts[echoId] = timeout;
  messageTimeout.value = newTimeouts;
  return newTimeouts;
};
const removeDataFor = payload => {
  const echoId = echoIdFrom(payload);
  if (!echoId) {
    throw new Error(`No echo id on payload ${payload}`);
  }
  if (echoId in messageData.value) {
    const newData = { ...messageData.value };
    delete newData[echoId];
    messageData.value = newData;
    return newData;
  }
  return messageData.value;
};
const setDataFor = (payload, data) => {
  const echoId = echoIdFrom(payload);
  if (!echoId) {
    throw new Error(`No echo id on payload ${payload}`);
  }
  const newData = { ...messageData.value };
  newData[echoId] = data;
  messageData.value = newData;
  return newData;
};
// "MESSAGE_NOTIFICATION": {
//   "FAILED": "Failed to send",
//     "RESENDING": "Resending...",
//     "SEND_SUCCESS": "Sent succesfully!"
// }

/**
 * @param message {Record<string, any> | FormData}
 * @returns {string | null}
 */
const bannerTextFor = message => {
  const echoId = echoIdFrom(message);
  const data = messageData.value[echoId];
  if (!data) {
    return null;
  }
  switch (data.status) {
    case 'failed':
      return t('MESSAGE_NOTIFICATION.FAILED');
    case 'resending':
      return t('MESSAGE_NOTIFICATION.RESENDING');
    case 'send_success':
      return t('MESSAGE_NOTIFICATION.SEND_SUCCESS');
    default:
      return null;
  }
};

/**
 * @param message {Record<string, any> | FormData}
 * @returns {string | null}
 */
const iconNameFor = message => {
  const echoId = echoIdFrom(message);
  const data = messageData.value[echoId];
  if (!data) {
    return null;
  }
  switch (data.status) {
    case 'failed':
      return 'warning';
    case 'resending':
      return 'arrow-redo';
    case 'send_success':
      return 'checkmark';
    default:
      return null;
  }
};
/**
 * @param message {Record<string, any> | FormData}
 * @returns {boolean | null}
 */
const canRetry = message => {
  const echoId = echoIdFrom(message);
  const data = messageData.value[echoId];
  if (!data) {
    return false;
  }
  switch (data.status) {
    case 'failed':
      return true;
    case 'resending':
      return false;
    case 'send_success':
      return false;
    default:
      return false;
  }
};
/**
 * @param message {Record<string, any> | FormData}
 * @returns {void}
 */
const attemptRetry = async message => {
  const echoId = echoIdFrom(message);
  if (!echoId) {
    console.error('No echo id on payload', message);
    return;
  }
  const oldData = messageData.value[echoId];
  if (oldData.status !== 'failed') {
    return;
  }
  setDataFor(message, { status: 'resending' });
  removeTimeoutFor(message);
  try {
    const response = await store.dispatch('sendMessageWithData', message);
    emitter.emit(BUS_EVENTS.MESSAGE_SENT, {
      payload: message,
      response,
    });
  } catch (error) {
    emitter.emit(BUS_EVENTS.MESSAGE_SEND_FAILURE, {
      payload: message,
      error,
    });
  }
};

/**
 * @param message {Record<string, any> | FormData}
 * @returns {void}
 */
const closeNotification = message => {
  removeDataFor(message);
  removeTimeoutFor(message);
  removeMessagePayload(message);
};

const onMessageSent = ({ payload, response }) => {
  const echoId = echoIdFrom(payload);
  if (!echoId) {
    console.error('No echo id on payload', payload);
    return;
  }
  if (!messageData.value[echoIdFrom(payload)]) {
    return;
  }
  upsertMessagePayload(payload);
  const timeout = setTimeout(
    () => closeNotification(payload),
    RECONNECTED_BANNER_TIMEOUT
  );
  upsertTimeoutFor(payload, timeout);
  setDataFor(payload, { status: 'send_success', response });
};
const onMessageSendFailure = ({ payload, error }) => {
  const echoId = echoIdFrom(payload);
  if (!echoId) {
    console.error("No echo id on payload", payload);
    return;
  }
  upsertMessagePayload(payload);
  removeTimeoutFor(payload);
  setDataFor(payload, { status: 'failed', error });
};

useEmitter(BUS_EVENTS.MESSAGE_SENT, onMessageSent);
useEmitter(BUS_EVENTS.MESSAGE_SEND_FAILURE, onMessageSendFailure);

onBeforeUnmount(() => {
  Object.values(messageTimeout.value).forEach(timeout => {
    if (timeout !== null && timeout !== undefined) {
      clearTimeout(timeout);
    }
  });
});
</script>

<template>
  <ul class="fixed z-51 top-16 left-2">
    <li
      v-for="message in messagePayloads"
      :key="echoIdFrom(message)"
      class="group mt-2"
    >
      <div
        class="relative flex items-center justify-between w-full px-2 py-1 bg-yellow-200 rounded-lg shadow-lg dark:bg-yellow-700"
      >
        <fluent-icon
          :icon="iconNameFor(message)"
          class="text-yellow-700/50 dark:text-yellow-50"
          size="18"
        />
        <span
          class="px-2 text-xs font-medium tracking-wide text-yellow-700/70 dark:text-yellow-50"
        >
          {{ bannerTextFor(message) }}
        </span>
        <woot-button
          v-if="canRetry(message)"
          :title="$t('NETWORK.BUTTON.REFRESH')"
          variant="clear"
          size="small"
          color-scheme="warning"
          icon="arrow-clockwise"
          @click="attemptRetry(message)"
        />
        <woot-button
          variant="clear"
          size="small"
          color-scheme="warning"
          icon="dismiss"
          @click="closeNotification(message)"
        />
      </div>
    </li>
  </ul>
</template>
